import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, ChevronRight, Shield } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gradient-to-b from-[#E3F2FD] to-white pt-16 pb-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="mx-auto w-16 h-16 bg-[#F0F7FF] rounded-full flex items-center justify-center mb-6">
              <FileText className="h-8 w-8 text-[#5E60CE]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#5E60CE] mb-6 font-display">Terms of Service</h1>
            <p className="text-lg text-[#4F86F7] mb-6 font-display">Last Updated: April 3, 2025</p>
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-500 font-display">
              <Link to="/" className="hover:text-[#5E60CE]">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <span>Terms of Service</span>
            </div>
          </motion.div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-[24px] shadow-lg p-6 sm:p-8 border border-[#E6EFFD]">
            <div className="prose prose-lg prose-headings:font-display prose-headings:text-[#5E60CE] prose-p:font-display prose-p:text-gray-600 prose-a:text-[#4F86F7] prose-a:font-display max-w-none">
              <p>
                Welcome to kuchipuchi! These Terms of Service ("Terms") govern your use of the kuchipuchi website, mobile applications, and services (collectively, the "Service"). Please read these Terms carefully.
              </p>
              
              <h2>1. Acceptance of Terms</h2>
              <p>
                By accessing or using the Service, you agree to be bound by these Terms and our Privacy Policy. If you disagree with any part of the terms, you may not access the Service.
              </p>
              
              <h2>2. Using Our Service</h2>
              <h3>2.1 Account Registration</h3>
              <p>
                You may need to create an account to use some features of our Service. You are responsible for maintaining the confidentiality of your account and password and for restricting access to your computer or device.
              </p>
              
              <h3>2.2 Age Restrictions</h3>
              <p>
                kuchipuchi is designed for use by parents, guardians, and educators to create stories for children. Users must be at least 18 years old to create an account. Children under 13 should only use the Service with parental supervision.
              </p>
              
              <h2>3. Content and Intellectual Property</h2>
              <h3>3.1 Your Content</h3>
              <p>
                When you create stories using our Service, you retain ownership of the personal information and preferences you provide. However, you grant us a license to use this information to generate and deliver the stories you request.
              </p>
              
              <h3>3.2 kuchipuchi Content</h3>
              <p>
                The Service and its original content, features, and functionality are owned by kuchipuchi and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
              </p>
              
              <h2>4. Acceptable Use</h2>
              <p>
                You agree not to use the Service:
              </p>
              <ul>
                <li>For any unlawful purpose or to solicit others to perform or participate in any unlawful acts</li>
                <li>To create or distribute inappropriate, offensive, or harmful content</li>
                <li>To impersonate or attempt to impersonate kuchipuchi, a kuchipuchi employee, or another user</li>
                <li>To engage in any activity that interferes with or disrupts the Service</li>
              </ul>
              
              <h2>5. Payment Terms</h2>
              <p>
                Some aspects of the Service may be offered for a fee. You agree to pay all fees and charges associated with your account on a timely basis and according to the pricing terms presented to you at the time of purchase.
              </p>
              
              <h2>6. Termination</h2>
              <p>
                We may terminate or suspend your account immediately, without prior notice or liability, for any reason, including without limitation if you breach the Terms.
              </p>
              
              <h2>7. Limitation of Liability</h2>
              <p>
                In no event shall kuchipuchi, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses.
              </p>
              
              <h2>8. Changes to Terms</h2>
              <p>
                We reserve the right to modify or replace these Terms at any time. We will provide notice of any changes by posting the new Terms on this page and updating the "last updated" date.
              </p>
              
              <h2>9. Contact Us</h2>
              <p>
                If you have any questions about these Terms, please contact us at:
              </p>
              <p>
                <a href="mailto:support@kuchipuchi.com">support@kuchipuchi.com</a>
              </p>
            </div>
          </div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-12 p-6 bg-[#F0F7FF] rounded-[24px] border border-[#E6EFFD] shadow-md"
          >
            <div className="flex items-start gap-4">
              <div className="mt-1 bg-white p-2 rounded-full">
                <Shield className="h-6 w-6 text-[#5E60CE]" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#5E60CE] font-display mb-2">Privacy Matters</h3>
                <p className="text-gray-600 font-display">
                  We care about protecting your information and your child's privacy. Learn more about how we handle your data in our <Link to="/privacy-policy" className="text-[#4F86F7] font-bold hover:underline">Privacy Policy</Link>.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Simple decorated footer */}
      <div className="py-10 bg-[#F8FBFF] border-t border-[#E6EFFD]">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-500 font-display mb-4">kuchipuchi — Magical stories for magical children</p>
          <div className="w-20 h-1 bg-gradient-to-r from-[#FF6B6B] to-[#FF9AA2] mx-auto rounded-full"></div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService; 